import React from 'react';
import styles from './Compliance.module.css';

const CompliancePage = () => {
  return (
    <div className={styles.complianceContainer}>
      <main className={styles.complianceContent}>
        <div className={styles.importantNotice}>
          <h1>Regulatory Compliance & Legal Framework</h1>
          <p className={styles.noticeText}>
            Comprehensive disclosure of our legal and regulatory obligations
          </p>
        </div>

        {/* Legal Status & Platform Model */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>1</span>
            Platform Legal Structure & Model
          </h2>
          
          <div className={styles.sectionContent}>
            <h3>1.1 Legal Entity Structure</h3>
            <div className={styles.structureGrid}>
              <div className={styles.structureItem}>
                <h4>Entity Type</h4>
                <p>Private Limited Company registered under Companies Act, 2013</p>
              </div>
              <div className={styles.structureItem}>
                <h4>Regulatory Status</h4>
                <p>Technology Platform - Not an NBFC or Banking Institution</p>
              </div>
              <div className={styles.structureItem}>
                <h4>Primary Role</h4>
                <p>Technology intermediary facilitating P2P lending agreements</p>
              </div>
              <div className={styles.structureItem}>
                <h4>Jurisdiction</h4>
                <p>Operates under Indian laws with courts in Bengaluru, Karnataka</p>
              </div>
            </div>

            <h3>1.2 Platform Model Disclosure</h3>
            <div className={styles.modelDisclosure}>
              <div className={styles.disclosureItem}>
                <div className={styles.disclosureHeader}>
                  <div className={styles.disclosureIcon}>⚠️</div>
                  <h5>What We Are</h5>
                </div>
                <ul>
                  <li>A technology platform connecting lenders and borrowers</li>
                  <li>Provider of digital documentation services</li>
                  <li>Facilitator of payment processing</li>
                  <li>Maintainer of social credit scoring system</li>
                </ul>
              </div>
              
              <div className={styles.disclosureItem}>
                <div className={styles.disclosureHeader}>
                  <div className={styles.disclosureIcon}>⛔</div>
                  <h5>What We Are NOT</h5>
                </div>
                <ul>
                  <li>Not a Non-Banking Financial Company (NBFC)</li>
                  <li>Not a bank or financial institution</li>
                  <li>Not a credit guarantee provider</li>
                  <li>Not an investment advisor</li>
                  <li>Not assuming credit risk on loans</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Regulatory Framework */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>2</span>
            Regulatory Framework & Compliance
          </h2>

          <div className={styles.sectionContent}>
            <h3>2.1 Primary Regulatory Authorities</h3>
            <div className={styles.regulatoryGrid}>
              <div className={styles.regulatoryCard}>
                <h4>Reserve Bank of India (RBI)</h4>
                <ul>
                  <li>Master Direction DNBR (PD) 077/03.10.119/2017-18</li>
                  <li>KYC Master Directions 2016</li>
                  <li>Fair Practices Code Guidelines</li>
                  <li>P2P Lending Platform Guidelines</li>
                </ul>
              </div>
              
              <div className={styles.regulatoryCard}>
                <h4>Ministry of Finance</h4>
                <ul>
                  <li>Prevention of Money Laundering Act, 2002</li>
                  <li>PML (Maintenance of Records) Rules, 2005</li>
                  <li>Income Tax Act, 1961 compliance</li>
                  <li>GST regulations and compliance</li>
                </ul>
              </div>
              
              <div className={styles.regulatoryCard}>
                <h4>Ministry of Electronics & IT</h4>
                <ul>
                  <li>Information Technology Act, 2000</li>
                  <li>Information Technology (Reasonable Security Practices) Rules, 2011</li>
                  <li>Digital Signature regulations</li>
                  <li>Data protection guidelines</li>
                </ul>
              </div>
            </div>

            <h3>2.2 Key Compliance Requirements</h3>
            <div className={styles.complianceTable}>
              <div className={styles.tableHeader}>
                <div>Requirement</div>
                <div>Regulatory Basis</div>
                <div>Our Implementation</div>
              </div>
              <div className={styles.tableRow}>
                <div>Customer Identification</div>
                <div>RBI KYC Directions</div>
                <div>Multi-tier verification with PAN, Aadhaar, video KYC</div>
              </div>
              <div className={styles.tableRow}>
                <div>Transaction Limits</div>
                <div>RBI P2P Guidelines</div>
                <div>₹2,00,000 per borrower, 10 loans per lender annually</div>
              </div>
              <div className={styles.tableRow}>
                <div>Interest Rate Caps</div>
                <div>State Money Lending Acts</div>
                <div>Platform-enforced caps based on borrower's state</div>
              </div>
              <div className={styles.tableRow}>
                <div>Data Protection</div>
                <div>IT Act & IT Rules</div>
                <div>AES-256 encryption, access controls, audit trails</div>
              </div>
              <div className={styles.tableRow}>
                <div>Tax Compliance</div>
                <div>Income Tax Act</div>
                <div>TDS deduction on interest, GST on platform fees</div>
              </div>
            </div>
          </div>
        </section>

        {/* Licensing & Permissions */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>3</span>
            Licensing Framework & Operational Permissions
          </h2>

          <div className={styles.sectionContent}>
            <h3>3.1 Licensing Requirements</h3>
            <div className={styles.licensingInfo}>
              <div className={styles.licenseStatus}>
                <h4>Platform License Status</h4>
                <div className={styles.statusBadge}>Technology Platform License</div>
                <p>
                  Operating under technology intermediary framework without 
                  requirement for NBFC license as per RBI P2P guidelines
                </p>
              </div>
              
              <div className={styles.licenseRequirements}>
                <h4>User Licensing Requirements</h4>
                <div className={styles.requirementsList}>
                  <div className={styles.requirementItem}>
                    <h5>Lenders</h5>
                    <p>No money lending license required for individuals lending to known persons</p>
                    <span className={styles.conditionTag}>Condition: Must comply with platform limits</span>
                  </div>
                  <div className={styles.requirementItem}>
                    <h5>Borrowers</h5>
                    <p>No specific license required for borrowing from known individuals</p>
                    <span className={styles.conditionTag}>Condition: Must disclose purpose truthfully</span>
                  </div>
                </div>
              </div>
            </div>

            <h3>3.2 State-wise Compliance</h3>
            <div className={styles.stateCompliance}>
              <div className={styles.stateCard}>
                <h5>Maharashtra</h5>
                <ul>
                  <li>Money Lending Act, 2014 compliance</li>
                  <li>Maximum interest: 12% per annum</li>
                  <li>Registration required for professional lenders</li>
                </ul>
              </div>
              <div className={styles.stateCard}>
                <h5>Delhi</h5>
                <ul>
                  <li>Delhi Money Lenders Act, 1976</li>
                  <li>Maximum interest: 12% per annum</li>
                  <li>No license for informal lending</li>
                </ul>
              </div>
              <div className={styles.stateCard}>
                <h5>Karnataka</h5>
                <ul>
                  <li>Karnataka Money Lenders Act, 1961</li>
                  <li>Maximum interest: 14% per annum</li>
                  <li>Exemption for non-business lending</li>
                </ul>
              </div>
              <div className={styles.stateCard}>
                <h5>Tamil Nadu</h5>
                <ul>
                  <li>Tamil Nadu Money Lenders Act, 1957</li>
                  <li>Maximum interest: 12% per annum</li>
                  <li>Registration exemption for small loans</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tax Compliance */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>4</span>
            Tax Compliance Framework
          </h2>

          <div className={styles.sectionContent}>
            <h3>4.1 Income Tax Obligations</h3>
            <div className={styles.taxFramework}>
              <div className={styles.taxCategory}>
                <h4>For Lenders</h4>
                <div className={styles.taxDetails}>
                  <div className={styles.taxItem}>
                    <h5>Interest Income</h5>
                    <p>Taxable under "Income from Other Sources"</p>
                    <span className={styles.taxRate}>As per applicable slab rate</span>
                  </div>
                  <div className={styles.taxItem}>
                    <h5>TDS Deduction</h5>
                    <p>Platform deducts TDS @ 10% on interest exceeding ₹5,000 annually</p>
                    <span className={styles.taxRate}>Section 194A of Income Tax Act</span>
                  </div>
                  <div className={styles.taxItem}>
                    <h5>Form 26AS</h5>
                    <p>TDS credits reflected in lender's Form 26AS</p>
                    <span className={styles.taxRate}>Auto-populated by platform</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.taxCategory}>
                <h4>For Borrowers</h4>
                <div className={styles.taxDetails}>
                  <div className={styles.taxItem}>
                    <h5>Loan Principal</h5>
                    <p>Not taxable as it's a capital receipt</p>
                    <span className={styles.taxRate}>Not applicable</span>
                  </div>
                  <div className={styles.taxItem}>
                    <h5>Interest Payment</h5>
                    <p>Interest paid is not tax-deductible for personal loans</p>
                    <span className={styles.taxRate}>Not deductible</span>
                  </div>
                  <div className={styles.taxItem}>
                    <h5>TDS Certificate</h5>
                    <p>Form 16A provided for TDS deducted on interest payments</p>
                    <span className={styles.taxRate}>Issued quarterly</span>
                  </div>
                </div>
              </div>
            </div>

            <h3>4.2 GST Compliance</h3>
            <div className={styles.gstCompliance}>
              <div className={styles.gstItem}>
                <h5>Platform Fees</h5>
                <p>18% GST applicable on all platform fees and commissions</p>
                <span className={styles.gstRate}>HSN Code: 997119</span>
              </div>
              <div className={styles.gstItem}>
                <h5>Invoice Generation</h5>
                <p>GST-compliant invoices generated for all transactions</p>
                <span className={styles.gstRate}>Auto-generated system</span>
              </div>
              <div className={styles.gstItem}>
                <h5>Input Tax Credit</h5>
                <p>Business users can claim ITC on GST paid for platform services</p>
                <span className={styles.gstRate}>Subject to conditions</span>
              </div>
            </div>
          </div>
        </section>

        {/* AML & KYC Framework */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>5</span>
            AML & KYC Compliance Framework
          </h2>

          <div className={styles.sectionContent}>
            <h3>5.1 Risk-Based Approach</h3>
            <div className={styles.amlFramework}>
              <div className={styles.amlProcess}>
                <h5>Customer Due Diligence</h5>
                <div className={styles.processSteps}>
                  <div className={styles.processStep}>
                    <span className={styles.stepNumber}>1</span>
                    <p>Identity verification via PAN & Aadhaar</p>
                  </div>
                  <div className={styles.processStep}>
                    <span className={styles.stepNumber}>2</span>
                    <p>Address verification and geolocation check</p>
                  </div>
                  <div className={styles.processStep}>
                    <span className={styles.stepNumber}>3</span>
                    <p>Relationship verification for P2P lending</p>
                  </div>
                  <div className={styles.processStep}>
                    <span className={styles.stepNumber}>4</span>
                    <p>Ongoing transaction monitoring</p>
                  </div>
                </div>
              </div>
              
              <div className={styles.reportingRequirements}>
                <h5>Regulatory Reporting</h5>
                <ul>
                  <li>Suspicious Transaction Reports (STRs) to FIU-IND</li>
                  <li>Cash Transaction Reports for transactions above ₹10 lakh</li>
                  <li>Annual compliance reports to RBI</li>
                  <li>Quarterly board reporting on AML compliance</li>
                </ul>
              </div>
            </div>

            <h3>5.2 Record Keeping Requirements</h3>
            <div className={styles.recordKeeping}>
              <div className={styles.recordCategory}>
                <h5>KYC Records</h5>
                <p>Maintained for 5 years after account closure</p>
                <span className={styles.recordDuration}>As per PMLA requirements</span>
              </div>
              <div className={styles.recordCategory}>
                <h5>Transaction Records</h5>
                <p>Maintained for 7 years from transaction date</p>
                <span className={styles.recordDuration}>As per RBI guidelines</span>
              </div>
              <div className={styles.recordCategory}>
                <h5>Compliance Reports</h5>
                <p>Maintained for 10 years</p>
                <span className={styles.recordDuration}>Internal policy</span>
              </div>
            </div>
          </div>
        </section>

        {/* Data Privacy & Security */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>6</span>
            Data Privacy & Security Compliance
          </h2>

          <div className={styles.sectionContent}>
            <h3>6.1 Data Protection Standards</h3>
            <div className={styles.dataProtection}>
              <div className={styles.protectionLayer}>
                <h5>Technical Security</h5>
                <ul>
                  <li>AES-256 encryption for data at rest and in transit</li>
                  <li>SSL/TLS 1.2+ for all communications</li>
                  <li>Regular vulnerability assessments and penetration testing</li>
                  <li>ISO 27001:2013 certified infrastructure</li>
                </ul>
              </div>
              
              <div className={styles.protectionLayer}>
                <h5>Organizational Security</h5>
                <ul>
                  <li>Role-based access control (RBAC)</li>
                  <li>Multi-factor authentication for sensitive operations</li>
                  <li>Employee background checks and security training</li>
                  <li>Third-party vendor security assessments</li>
                </ul>
              </div>
            </div>

            <h3>6.2 Privacy Compliance</h3>
            <div className={styles.privacyCompliance}>
              <div className={styles.privacyPrinciple}>
                <h5>Purpose Limitation</h5>
                <p>Data collected only for specified, legitimate purposes</p>
              </div>
              <div className={styles.privacyPrinciple}>
                <h5>Data Minimization</h5>
                <p>Collection limited to necessary information for service delivery</p>
              </div>
              <div className={styles.privacyPrinciple}>
                <h5>Consent Management</h5>
                <p>Explicit consent obtained for data processing activities</p>
              </div>
              <div className={styles.privacyPrinciple}>
                <h5>User Rights</h5>
                <p>Right to access, rectify, and delete personal data</p>
              </div>
            </div>
          </div>
        </section>

        {/* User Protection Framework */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>7</span>
            User Protection & Grievance Redressal
          </h2>

          <div className={styles.sectionContent}>
            <h3>7.1 Fair Practices Code</h3>
            <div className={styles.fairPractices}>
              <div className={styles.practiceItem}>
                <h5>Transparent Pricing</h5>
                <p>All fees and charges disclosed upfront before transaction</p>
              </div>
              <div className={styles.practiceItem}>
                <h5>Equal Treatment</h5>
                <p>No discrimination based on gender, religion, caste, or region</p>
              </div>
              <div className={styles.practiceItem}>
                <h5>Privacy Protection</h5>
                <p>User data not shared without consent except as required by law</p>
              </div>
              <div className={styles.practiceItem}>
                <h5>Clear Communication</h5>
                <p>All terms and conditions communicated in simple language</p>
              </div>
            </div>

            <h3>7.2 Grievance Redressal Mechanism</h3>
            <div className={styles.grievanceMechanism}>
              <div className={styles.grievanceLevel}>
                <h5>Level 1: Platform Support</h5>
                <p>Initial complaint resolution through customer support</p>
                <span className={styles.resolutionTime}>Resolution within 48 hours</span>
              </div>
              <div className={styles.grievanceLevel}>
                <h5>Level 2: Nodal Officer</h5>
                <p>Escalation to designated nodal officer</p>
                <span className={styles.resolutionTime}>Resolution within 7 working days</span>
              </div>
              <div className={styles.grievanceLevel}>
                <h5>Level 3: Appellate Authority</h5>
                <p>Appeal to appellate authority if unresolved</p>
                <span className={styles.resolutionTime}>Resolution within 15 working days</span>
              </div>
              <div className={styles.grievanceLevel}>
                <h5>Level 4: Regulatory Authority</h5>
                <p>Approach RBI Ombudsman or consumer court</p>
                <span className={styles.resolutionTime}>As per regulatory timelines</span>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Disclosure & Limitations */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>8</span>
            Risk Disclosures & Platform Limitations
          </h2>

          <div className={styles.sectionContent}>
            <h3>8.1 User Risk Disclosures</h3>
            <div className={styles.riskDisclosures}>
              <div className={styles.riskItem}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.credit}`}></div>
                  <h5>Credit Risk</h5>
                </div>
                <p>Lenders bear complete credit risk of borrower default</p>
                <p className={styles.riskNote}>Platform does not guarantee repayments</p>
              </div>
              
              <div className={styles.riskItem}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.operational}`}></div>
                  <h5>Operational Risk</h5>
                </div>
                <p>System failures, technical glitches, or service interruptions</p>
                <p className={styles.riskNote}>Platform implements redundancy measures</p>
              </div>
              
              <div className={styles.riskItem}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.regulatory}`}></div>
                  <h5>Regulatory Risk</h5>
                </div>
                <p>Changes in laws affecting P2P lending operations</p>
                <p className={styles.riskNote}>Platform monitors regulatory developments</p>
              </div>
              
              <div className={styles.riskItem}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.liquidity}`}></div>
                  <h5>Liquidity Risk</h5>
                </div>
                <p>Difficulty in finding matching lenders or borrowers</p>
                <p className={styles.riskNote}>Platform facilitates but doesn't guarantee matches</p>
              </div>
            </div>

            <h3>8.2 Platform Limitations</h3>
            <div className={styles.platformLimitations}>
              <div className={styles.limitation}>
                <h5>No Credit Assessment</h5>
                <p>Platform does not assess creditworthiness of borrowers</p>
              </div>
              <div className={styles.limitation}>
                <h5>No Recovery Guarantee</h5>
                <p>Platform assists but does not guarantee recovery of defaulted loans</p>
              </div>
              <div className={styles.limitation}>
                <h5>No Investment Advice</h5>
                <p>Platform does not provide investment or lending advice</p>
              </div>
              <div className={styles.limitation}>
                <h5>Limited Dispute Resolution</h5>
                <p>Platform role limited to documentation and payment facilitation</p>
              </div>
            </div>
          </div>
        </section>

        {/* Governance & Oversight */}
        <section className={styles.complianceSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>9</span>
            Governance, Oversight & Continuous Compliance
          </h2>

          <div className={styles.sectionContent}>
            <h3>9.1 Governance Structure</h3>
            <div className={styles.governanceStructure}>
              <div className={styles.governanceLevel}>
                <h5>Board of Directors</h5>
                <p>Overall responsibility for compliance framework</p>
                <span className={styles.meetingFrequency}>Quarterly reviews</span>
              </div>
              <div className={styles.governanceLevel}>
                <h5>Risk & Compliance Committee</h5>
                <p>Oversight of compliance implementation and monitoring</p>
                <span className={styles.meetingFrequency}>Monthly meetings</span>
              </div>
              <div className={styles.governanceLevel}>
                <h5>Compliance Department</h5>
                <p>Day-to-day compliance operations and monitoring</p>
                <span className={styles.meetingFrequency}>Continuous operations</span>
              </div>
              <div className={styles.governanceLevel}>
                <h5>Internal Audit</h5>
                <p>Independent assessment of compliance effectiveness</p>
                <span className={styles.meetingFrequency}>Half-yearly audits</span>
              </div>
            </div>

            <h3>9.2 Continuous Compliance Monitoring</h3>
            <div className={styles.complianceMonitoring}>
              <div className={styles.monitoringActivity}>
                <h5>Regulatory Updates</h5>
                <p>Continuous monitoring of regulatory changes and updates</p>
              </div>
              <div className={styles.monitoringActivity}>
                <h5>Policy Reviews</h5>
                <p>Annual review and update of all policies and procedures</p>
              </div>
              <div className={styles.monitoringActivity}>
                <h5>Training Programs</h5>
                <p>Regular compliance training for employees and users</p>
              </div>
              <div className={styles.monitoringActivity}>
                <h5>Third-party Audits</h5>
                <p>Annual external audits of compliance framework</p>
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <div className={styles.updateNotice}>
          <p><strong>Last Updated:</strong> October 15, 2025</p>
          <p><strong>Next Review:</strong> March 15, 2026</p>
          <p className={styles.disclaimerNote}>
            This compliance disclosure is for informational purposes only and does not constitute legal advice.
            Users should consult with legal counsel for specific compliance requirements.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CompliancePage;